var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import '../card/BasicCard.js';
const DEFAULT_COLORS = { border: '#2196F3', bg: '#E3F2FD', icon: '#1976D2' };
const VARIANT_COLORS = {
    info: DEFAULT_COLORS,
    success: { border: '#4CAF50', bg: '#E8F5E9', icon: '#388E3C' },
    warning: { border: '#FF9800', bg: '#FFF3E0', icon: '#F57C00' },
    error: { border: '#F44336', bg: '#FFEBEE', icon: '#D32F2F' },
};
const DEFAULT_ICON = '\u2139\uFE0F';
const VARIANT_ICONS = {
    info: DEFAULT_ICON,
    success: '\u2705',
    warning: '\u26A0\uFE0F',
    error: '\u274C',
};
let NotificationCard = class NotificationCard extends LitElement {
    constructor() {
        super(...arguments);
        this.isRead = false;
        this.isMarking = false;
    }
    connectedCallback() {
        super.connectedCallback();
        this.isRead = this.notification?.read ?? false;
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('notification')) {
            this.isRead = this.notification?.read ?? false;
        }
    }
    updated() {
        if (this.isRead) {
            this.classList.add('read');
        }
        else {
            this.classList.remove('read');
        }
    }
    get variant() {
        return this.notification?.style?.type ?? 'info';
    }
    get colors() {
        return VARIANT_COLORS[this.variant] ?? DEFAULT_COLORS;
    }
    get variantIcon() {
        return VARIANT_ICONS[this.variant] ?? DEFAULT_ICON;
    }
    formatRelativeTime(dateString) {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffMin < 1)
            return msg('Just now', { id: 'notification.time.justNow' });
        if (diffMin < 60)
            return `${diffMin}min`;
        if (diffHours < 24)
            return `${diffHours}h`;
        if (diffDays < 7)
            return `${diffDays}d`;
        return date.toLocaleDateString();
    }
    handleCardClick() {
        this.dispatchEvent(new CustomEvent('notification-click', {
            detail: this.notification,
            bubbles: true,
            composed: true,
        }));
    }
    handleLinkClick(e) {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('notification-link-click', {
            detail: { notification: this.notification, link: this.notification.link },
            bubbles: true,
            composed: true,
        }));
    }
    async handleMarkAsRead(e) {
        e.stopPropagation();
        if (this.isRead || this.isMarking || !this.apiService)
            return;
        this.isMarking = true;
        try {
            await this.apiService.put(`/notification-status/${this.notification.notification_id}`, { read: true });
            this.isRead = true;
            this.dispatchEvent(new CustomEvent('notification-read', {
                detail: this.notification,
                bubbles: true,
                composed: true,
            }));
        }
        catch (err) {
            console.error('Failed to mark notification as read:', err);
        }
        finally {
            this.isMarking = false;
        }
    }
    getLinkLabel() {
        const link = this.notification.link;
        if (!link)
            return '';
        if (link.type === 'map')
            return msg('Open map', { id: 'notification.link.openMap' });
        if (link.type === 'module')
            return msg('Open module', { id: 'notification.link.openModule' });
        if (link.type === 'url')
            return msg('Open link', { id: 'notification.link.openUrl' });
        return msg('Open', { id: 'notification.open' });
    }
    render() {
        if (!this.notification)
            return nothing;
        const { colors } = this;
        return html `
      <basic-card
        size="small"
        style="--notification-border-color: ${colors.border}; border-left-color: ${colors.border};"
        @click="${this.handleCardClick}"
      >
        <div class="notification-card__body">
          <span class="notification-card__icon">${this.variantIcon}</span>

          <div class="notification-card__content">
            <div class="notification-card__header">
              <h4 class="notification-card__title">${this.notification.title}</h4>
              <span class="notification-card__time">
                ${this.formatRelativeTime(this.notification.created_at)}
              </span>
            </div>

            ${this.notification.description
            ? html `<p class="notification-card__description">${this.notification.description}</p>`
            : nothing}

            <div class="notification-card__actions">
              ${!this.isRead
            ? html `
                    <span class="notification-card__unread-dot"></span>
                    <button
                      class="mark-read-btn"
                      @click="${this.handleMarkAsRead}"
                      ?disabled="${this.isMarking}"
                    >
                      ${this.isMarking
                ? msg('Marking...', { id: 'notification.marking' })
                : msg('Mark as read', { id: 'notification.markAsRead' })}
                    </button>
                  `
            : nothing}

              ${this.notification.link
            ? html `<span class="notification-card__link-indicator" @click="${this.handleLinkClick}">${this.getLinkLabel()} &rarr;</span>`
            : nothing}
            </div>
          </div>
        </div>
      </basic-card>
    `;
    }
};
NotificationCard.styles = css `
    :host {
      display: block;
    }

    basic-card {
      border-left: 4px solid var(--notification-border-color, #2196F3);
      cursor: pointer;
      transition: opacity 0.2s, box-shadow 0.2s;
    }

    basic-card:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    }

    :host(.read) basic-card {
      opacity: 0.6;
    }

    .notification-card__body {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }

    .notification-card__icon {
      font-size: 1.25rem;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .notification-card__content {
      flex: 1;
      min-width: 0;
    }

    .notification-card__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }

    .notification-card__title {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: #333;
    }

    .notification-card__time {
      font-size: 0.7rem;
      color: #999;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .notification-card__description {
      margin: 4px 0 0;
      font-size: 0.8rem;
      color: #666;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .notification-card__actions {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 6px;
    }

    .notification-card__unread-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--notification-border-color, #2196F3);
      flex-shrink: 0;
    }

    .mark-read-btn {
      background: none;
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 2px 8px;
      font-size: 0.7rem;
      color: #666;
      cursor: pointer;
      transition: background 0.15s;
    }

    .mark-read-btn:hover {
      background: rgba(0, 0, 0, 0.05);
    }

    .mark-read-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .notification-card__link-indicator {
      font-size: 0.7rem;
      color: #1976D2;
      cursor: pointer;
    }

    .notification-card__link-indicator:hover {
      text-decoration: underline;
    }
  `;
__decorate([
    property({ type: Object })
], NotificationCard.prototype, "notification", void 0);
__decorate([
    property({ type: Object })
], NotificationCard.prototype, "apiService", void 0);
__decorate([
    state()
], NotificationCard.prototype, "isRead", void 0);
__decorate([
    state()
], NotificationCard.prototype, "isMarking", void 0);
NotificationCard = __decorate([
    localized(),
    customElement('notification-card')
], NotificationCard);
export { NotificationCard };
//# sourceMappingURL=NotificationCard.js.map